import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { useHubToast } from "@/components/ui/HubToast";
import { cn } from "@/lib/utils";
import { newProductExecuteWaitAdvisory } from "./newProductExecuteWaitAdvisory";
import { newProductExecuteErrorText } from "./newProductExecuteError";

const PAGE_HOVER_HINT =
  "Add a new FI-3C, FI-5A, FI-5A-W, FP-UG, FP-OH, FP-OC, or ABB finished-good SKU. The hub validates BoM parse rules and generate_description_parts, creates or restores Megaventory products for the finished good and BoM components, replays item ingest, and pushes the BoM revision.";

type ProductFamily = "fi-3c" | "fi-5a" | "fi-5a-w";

type PreviewResponse = {
  ok: true;
  preview: {
    sku: string;
    family: ProductFamily;
    familyLabel: string;
    templateId: number;
    templateName: string;
    templateRevision: number;
    description: {
      full_description: string;
      description_field: string;
      variant_field: string;
    };
    megaventoryCategory: string;
    megaventoryProductType: string;
    megaventoryIsSellable: boolean;
    megaventoryIsPurchaseable: boolean;
    hubFlags: {
      manufacture: boolean;
      serialize: boolean;
      build_to_order: boolean;
      for_sale: boolean;
      for_sale_deployed: boolean;
    };
    finishedGoodMegaventoryPresence: "active" | "deleted" | "missing";
    bomComponents: Array<{
      sku: string;
      quantity: string | number | null;
      megaventoryPresence: "active" | "deleted" | "missing";
      localMvId: number | null;
    }>;
    componentGaps: Array<{ sku: string; reason: string }>;
  };
};

type WorkflowStep = {
  key: string;
  label: string;
  status: "pending" | "ok" | "skipped" | "failed";
  detail?: string;
};

const FAMILY_OPTIONS: Array<{ value: ProductFamily; label: string; hint: string }> = [
  { value: "fi-3c", label: "FI-3C", hint: "Underground FCI (FI-3C, FP-UG, ABB-)" },
  { value: "fi-5a", label: "FI-5A", hint: "Overhead FCI (FI-5A, FP-OH, ABB-)" },
  { value: "fi-5a-w", label: "FI-5A-W", hint: "Wireless overhead FCI (FI-5A-W, FP-OC, ABB-)" },
];

function presenceLabel(value: "active" | "deleted" | "missing"): string {
  if (value === "active") return "Active";
  if (value === "deleted") return "Deleted in Megaventory";
  return "Missing";
}

function presenceClass(value: "active" | "deleted" | "missing"): string {
  if (value === "active") return "text-emerald-700";
  if (value === "deleted") return "text-amber-700";
  return "text-rose-700";
}

function flagLabel(value: boolean): string {
  return value ? "Yes" : "No";
}

function flagClass(value: boolean): string {
  return value ? "text-emerald-700" : "text-slate-700";
}

export function NewProductPage() {
  const hubWritesAllowed = useHubModuleWrite("inside_sales");
  const { showToast } = useHubToast();
  const [family, setFamily] = useState<ProductFamily | "">("");
  const [sku, setSku] = useState("");
  const [preview, setPreview] = useState<PreviewResponse["preview"] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [executeSteps, setExecuteSteps] = useState<WorkflowStep[] | null>(null);
  const [executeStartedAtMs, setExecuteStartedAtMs] = useState<number | null>(null);
  const [executeElapsedSeconds, setExecuteElapsedSeconds] = useState(0);
  const [executeError, setExecuteError] = useState<string | null>(null);

  const previewMutation = useMutation({
    mutationFn: async () => {
      const body: { sku: string; family?: ProductFamily } = { sku: sku.trim() };
      if (family !== "") {
        body.family = family;
      }
      return apiFetch<PreviewResponse>("/api/inside-sales/new-product/preview", {
        method: "POST",
        body,
      });
    },
    onSuccess: (response) => {
      setPreview(response.preview);
      setPreviewError(null);
      setExecuteSteps(null);
    },
    onError: (error: unknown) => {
      setPreview(null);
      if (error instanceof ApiError) {
        const body = error.body as { error?: string } | undefined;
        setPreviewError(body?.error ?? error.message);
      } else if (error instanceof Error) {
        setPreviewError(error.message);
      } else {
        setPreviewError(String(error));
      }
      setExecuteSteps(null);
    },
  });

  const executeMutation = useMutation({
    mutationFn: async () => {
      const body: { sku: string; family?: ProductFamily } = { sku: sku.trim() };
      if (family !== "") {
        body.family = family;
      }
      return apiFetch<{ ok: true; steps: WorkflowStep[]; preview: PreviewResponse["preview"] }>(
        "/api/inside-sales/new-product/execute",
        { method: "POST", body, skipSlowRequestBanner: true, skipRetries: true },
      );
    },
    onSuccess: (response) => {
      setPreview(response.preview);
      setExecuteSteps(response.steps);
      setPreviewError(null);
      setExecuteError(null);
      showToast({ tone: "success", message: `Created workflow completed for ${response.preview.sku}.` });
    },
    onError: (error: unknown) => {
      const message = newProductExecuteErrorText(error);
      setExecuteError(message);
      if (error instanceof ApiError) {
        const body = error.body as
          | { error?: string; steps?: WorkflowStep[]; preview?: PreviewResponse["preview"] | null }
          | undefined;
        setExecuteSteps(body?.steps ?? null);
        if (body?.preview != null) {
          setPreview(body.preview);
        }
      }
      showToast({ tone: "error", message });
    },
  });

  useEffect(() => {
    setPreview(null);
    setPreviewError(null);
    setExecuteSteps(null);
    setExecuteError(null);
    executeMutation.reset();
    previewMutation.reset();
    setExecuteStartedAtMs(null);
    setExecuteElapsedSeconds(0);
  }, [sku, family]);

  useEffect(() => {
    if (!executeMutation.isPending || executeStartedAtMs == null) {
      return;
    }
    const tick = () => {
      setExecuteElapsedSeconds(Math.max(0, Math.floor((Date.now() - executeStartedAtMs) / 1000)));
    };
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [executeMutation.isPending, executeStartedAtMs]);

  const componentsNeedingAttention = useMemo(() => {
    if (preview == null) {
      return 0;
    }
    return preview.bomComponents.filter((row) => row.megaventoryPresence !== "active").length;
  }, [preview]);

  return (
    <div className="flex flex-col gap-6">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>New product</PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Finished-good SKU</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600">
            Enter the full Megaventory SKU (for example{" "}
            <span className="font-mono text-xs">FI-3C S04BAM</span>,{" "}
            <span className="font-mono text-xs">FP-UG-S08BAM</span>, or{" "}
            <span className="font-mono text-xs">ABB-FI-3C S08BAM</span>). The hub infers the product family from the
            prefix when you do not select one.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="new-product-family">Product family</Label>
              <select
                id="new-product-family"
                className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                value={family}
                onChange={(event) => setFamily(event.target.value as ProductFamily | "")}
              >
                <option value="">Infer from SKU prefix</option>
                {FAMILY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} — {option.hint}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-product-sku">SKU</Label>
              <Input
                id="new-product-sku"
                value={sku}
                onChange={(event) => setSku(event.target.value)}
                placeholder="FI-3C S04BAM"
                spellCheck={false}
                autoComplete="off"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={sku.trim().length === 0 || previewMutation.isPending}
              onClick={() => previewMutation.mutate()}
            >
              {previewMutation.isPending ? "Validating…" : "Validate and preview"}
            </Button>
            <Button
              type="button"
              variant={
                executeMutation.isPending
                  ? "primary"
                  : executeMutation.isError
                    ? "danger"
                    : executeMutation.isSuccess
                      ? "success"
                      : "primary"
              }
              disabled={
                !hubWritesAllowed ||
                preview == null ||
                executeMutation.isPending ||
                executeMutation.isSuccess
              }
              onClick={() => {
                setExecuteStartedAtMs(Date.now());
                setExecuteElapsedSeconds(0);
                setExecuteError(null);
                executeMutation.mutate();
              }}
            >
              {executeMutation.isPending
                ? "Running workflow…"
                : executeMutation.isError
                  ? "Create in Megaventory failed"
                  : executeMutation.isSuccess
                    ? "Created in Megaventory"
                    : "Create in Megaventory"}
            </Button>
            </div>
            {executeMutation.isPending ? (
              <p className="max-w-xl text-sm text-slate-600" role="status">
                {newProductExecuteWaitAdvisory(executeElapsedSeconds)}
              </p>
            ) : null}
          </div>
          {!hubWritesAllowed ? (
            <p className="text-sm text-slate-500">Inside Sales write access is required to run the create workflow.</p>
          ) : null}
          {previewError != null ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {previewError}
            </div>
          ) : null}
          {executeError != null ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
              {executeError}
            </div>
          ) : null}
        </CardBody>
      </Card>

      {preview != null ? (
        <Card>
          <CardHeader>
            <CardTitle>Preview — {preview.sku}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4 text-sm">
            <dl className="grid gap-3 md:grid-cols-2">
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Family</dt>
                <dd className="mt-1 font-medium text-slate-900">{preview.familyLabel}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">BoM template</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {preview.templateName} (revision {preview.templateRevision})
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Megaventory category</dt>
                <dd className="mt-1 text-slate-900">{preview.megaventoryCategory}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Megaventory product type</dt>
                <dd className="mt-1 font-medium text-slate-900">{preview.megaventoryProductType}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Finished good in MV</dt>
                <dd className={cn("mt-1 font-medium", presenceClass(preview.finishedGoodMegaventoryPresence))}>
                  {presenceLabel(preview.finishedGoodMegaventoryPresence)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Megaventory sellable</dt>
                <dd className={cn("mt-1 font-medium", flagClass(preview.megaventoryIsSellable))}>
                  {flagLabel(preview.megaventoryIsSellable)}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Megaventory purchasable</dt>
                <dd className={cn("mt-1 font-medium", flagClass(preview.megaventoryIsPurchaseable))}>
                  {flagLabel(preview.megaventoryIsPurchaseable)}
                </dd>
              </div>
            </dl>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">Hub catalog flags</h3>
              <p className="mt-1 text-xs text-slate-500">
                Values written to public.sku on validate. Finished goods are marked manufacture, serialize, build to
                order, for sale, and for sale deployed.
              </p>
              <dl className="mt-3 grid gap-3 md:grid-cols-3">
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Manufacture</dt>
                  <dd className={cn("mt-1 font-medium", flagClass(preview.hubFlags.manufacture))}>
                    {flagLabel(preview.hubFlags.manufacture)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Serialize</dt>
                  <dd className={cn("mt-1 font-medium", flagClass(preview.hubFlags.serialize))}>
                    {flagLabel(preview.hubFlags.serialize)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Build to order</dt>
                  <dd className={cn("mt-1 font-medium", flagClass(preview.hubFlags.build_to_order))}>
                    {flagLabel(preview.hubFlags.build_to_order)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">For sale</dt>
                  <dd className={cn("mt-1 font-medium", flagClass(preview.hubFlags.for_sale))}>
                    {flagLabel(preview.hubFlags.for_sale)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">For sale deployed</dt>
                  <dd className={cn("mt-1 font-medium", flagClass(preview.hubFlags.for_sale_deployed))}>
                    {flagLabel(preview.hubFlags.for_sale_deployed)}
                  </dd>
                </div>
              </dl>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">Description</h3>
              <p className="mt-1 text-slate-900">{preview.description.description_field}</p>
              <p className="mt-1 text-slate-700">{preview.description.variant_field}</p>
              <p className="mt-2 text-xs text-slate-500">{preview.description.full_description}</p>
            </div>
            <div>
              <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                BoM components ({preview.bomComponents.length})
                {componentsNeedingAttention > 0
                  ? ` — ${componentsNeedingAttention} need create or undelete in Megaventory`
                  : ""}
              </h3>
              <p className="mb-2 text-xs text-slate-500">
                Create undeletes deleted components and inserts missing ones, then pushes the finished-good BoM.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-slate-200 text-left text-xs">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="border border-slate-200 px-2 py-1.5 font-medium">Component SKU</th>
                      <th className="border border-slate-200 px-2 py-1.5 font-medium">Qty</th>
                      <th className="border border-slate-200 px-2 py-1.5 font-medium">Megaventory</th>
                      <th className="border border-slate-200 px-2 py-1.5 font-medium">public.sku.mv_id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.bomComponents.map((row) => (
                      <tr key={row.sku}>
                        <td className="border border-slate-200 px-2 py-1 font-mono">{row.sku}</td>
                        <td className="border border-slate-200 px-2 py-1 tabular-nums">{String(row.quantity ?? "")}</td>
                        <td className={cn("border border-slate-200 px-2 py-1", presenceClass(row.megaventoryPresence))}>
                          {presenceLabel(row.megaventoryPresence)}
                        </td>
                        <td className="border border-slate-200 px-2 py-1 tabular-nums">
                          {row.localMvId ?? "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {executeSteps != null && executeSteps.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Workflow log</CardTitle>
          </CardHeader>
          <CardBody>
            <ul className="space-y-2 text-sm">
              {executeSteps.map((step) => (
                <li key={step.key} className="flex flex-col gap-0.5 border-b border-slate-100 pb-2 last:border-0">
                  <span
                    className={cn(
                      "font-medium",
                      step.status === "ok" && "text-emerald-700",
                      step.status === "failed" && "text-rose-700",
                      step.status === "skipped" && "text-slate-500",
                    )}
                  >
                    {step.label} — {step.status}
                  </span>
                  {step.detail != null && step.detail.length > 0 ? (
                    <span className="text-slate-600">{step.detail}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
